import { useEffect, useState, type ReactNode } from 'react'
import { ArrowUpRight, Link2, Save } from 'lucide-react'
import {
  useShipmentsApi,
  type ShipmentWithLocation,
} from 'src/api/db/apiShipment'
import { useOrderNotifications } from 'src/api/server/useOrderNotifications'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Card } from 'src/shared/components/ui/card'
import { Input } from 'src/shared/components/ui/input'
import { Textarea } from 'src/shared/components/ui/textarea'
import { cn } from 'src/shared/components/ui/utils'
import {
  DELIVERED_STATUS,
  SHIPMENT_STATUS_OPTIONS,
  effectiveQuoteStatus,
} from 'src/shared/consts/quoteStatus'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { renderTextWithLinks } from 'src/shared/utils/renderTextWithLinks'
import { formatDeliveryDate } from '../../../OrderSummary/components/ShippingCard/utils'

type TimelineStep = {
  title: string
  date: string | null
  detail?: ReactNode
  state: 'done' | 'current' | 'upcoming'
}

type StatusTone = 'transit' | 'delivered' | 'packed'

const STATUS_TONE: Record<
  StatusTone,
  { live: string; ring: string; fill: string; line: string }
> = {
  transit: {
    live: 'bg-blue-500',
    ring: 'border-blue-500',
    fill: 'bg-blue-500',
    line: 'bg-blue-500/25',
  },
  delivered: {
    live: 'bg-emerald-500',
    ring: 'border-emerald-500',
    fill: 'bg-emerald-500',
    line: 'bg-emerald-500/25',
  },
  packed: {
    live: 'bg-amber-500',
    ring: 'border-amber-500',
    fill: 'bg-amber-500',
    line: 'bg-amber-500/25',
  },
}

const trackingCtaLabel = (
  url: string,
  t: (a: string, b?: string) => string
) => {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '').toLowerCase()
    if (host.includes('dhl.')) return t('Track on DHL', 'Spåra hos DHL')
    if (host.includes('ups.')) return t('Track on UPS', 'Spåra hos UPS')
    if (host.includes('fedex')) return t('Track on FedEx', 'Spåra hos FedEx')
    if (host.includes('postnord'))
      return t('Track on PostNord', 'Spåra hos PostNord')
  } catch {
    // Fall through to generic label.
  }
  return t('Track shipment', 'Spåra leverans')
}

const TimelineMarker = ({
  state,
  tone,
}: {
  state: TimelineStep['state']
  tone: StatusTone
}) => {
  const color = STATUS_TONE[tone]

  if (state === 'current') {
    return (
      <div className="relative mt-0.5 flex h-3.5 w-3.5 items-center justify-center">
        {tone !== 'delivered' ? (
          <span
            className={cn(
              'absolute inset-0 rounded-full opacity-70 animate-ping',
              color.fill
            )}
          />
        ) : null}
        <span
          className={cn(
            'absolute inset-0 rounded-full border bg-white',
            color.ring
          )}
        />
        <span className={cn('relative h-1.5 w-1.5 rounded-full', color.fill)} />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'mt-1.5 rounded-full',
        state === 'done' && cn('h-2 w-2', color.fill),
        state === 'upcoming' && 'h-2 w-2 border border-border bg-white'
      )}
    />
  )
}

const DeliveryTimeline = ({
  steps,
  tone,
}: {
  steps: TimelineStep[]
  tone: StatusTone
}) => (
  <Column>
    {steps.map((step, index) => (
      <Row key={step.title} className="items-stretch gap-3">
        <Column className="w-3.5 shrink-0 items-center">
          <TimelineMarker state={step.state} tone={tone} />
          {index < steps.length - 1 ? (
            <div
              className={cn(
                'mt-1 w-px flex-1',
                step.state === 'upcoming' ? 'bg-border' : STATUS_TONE[tone].line
              )}
            />
          ) : null}
        </Column>
        <Column
          className={cn(
            'min-w-0 flex-1 gap-0.5',
            index < steps.length - 1 && 'pb-4'
          )}
        >
          <Row className="items-baseline gap-2">
            <span
              className={cn(
                'text-sm',
                step.state === 'current' && 'font-semibold text-[#1A1C1E]',
                step.state === 'done' && 'font-medium text-[#1A1C1E]',
                step.state === 'upcoming' && 'text-muted-foreground'
              )}
            >
              {step.title}
            </span>
            {step.date ? (
              <span
                className={cn(
                  'text-[13px]',
                  step.state === 'upcoming'
                    ? 'text-muted-foreground'
                    : 'text-[#6B7280]'
                )}
              >
                {step.date}
              </span>
            ) : null}
          </Row>
          {step.detail ? (
            <div className="max-w-md text-[13px] leading-relaxed text-[#6B7280]">
              {step.detail}
            </div>
          ) : null}
        </Column>
      </Row>
    ))}
  </Column>
)

export const DeliveryTrackingHero = ({
  quoteId,
  quotePublicId,
  quoteStatus,
  demoShipment,
}: {
  quoteId: number
  quotePublicId?: string | null
  quoteStatus?: string | null
  demoShipment?: ShipmentWithLocation
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const isAdmin = useIsAdmin()
  const orderNotifications = useOrderNotifications()
  const { shipments, isLoading, createShipment, updateShipment } =
    useShipmentsApi({ quote_id: demoShipment ? undefined : quoteId })

  const shipment: ShipmentWithLocation | undefined =
    demoShipment ?? shipments[0]
  const [trackingLink, setTrackingLink] = useState('')
  const [arrivalDate, setArrivalDate] = useState('')
  const [customerNote, setCustomerNote] = useState('')

  useEffect(() => {
    setTrackingLink(shipment?.shipment_url ?? '')
    setArrivalDate(shipment?.shipping_date_end ?? '')
    setCustomerNote(shipment?.notes ?? '')
  }, [
    shipment?.id,
    shipment?.shipment_url,
    shipment?.shipping_date_end,
    shipment?.notes,
  ])

  const quoteDelivered = effectiveQuoteStatus(quoteStatus) === DELIVERED_STATUS
  const delivered = shipment?.status === 'delivered' || quoteDelivered
  const inTransit =
    shipment?.status === 'in_transit' || shipment?.status === 'out_for_delivery'
  const hasShipment = Boolean(shipment) || quoteDelivered
  const etaLabel = formatDeliveryDate(
    shipment?.shipping_date_end || arrivalDate || null
  )
  const hasTracking = Boolean(trackingLink.trim())
  const isSaving = createShipment.isPending || updateShipment.isPending
  const trackingNumber = shipment?.shipment_id?.trim() || null

  const headline = delivered
    ? t('Delivered', 'Levererad')
    : inTransit
      ? t('On the way', 'På väg')
      : hasShipment
        ? t('Packed', 'Packad')
        : t('Preparing shipment', 'Förbereder leverans')

  const packedDate = formatDeliveryDate(
    shipment?.shipping_date_start || shipment?.created_at
  )
  const currentStepIndex = delivered ? 2 : inTransit ? 1 : 0
  const statusTone: StatusTone = delivered
    ? 'delivered'
    : inTransit
      ? 'transit'
      : 'packed'
  const noteText = customerNote.trim()
  const noteNode = noteText
    ? renderTextWithLinks(noteText, {
        linkClassName:
          'text-[13px] text-[#6B7280] underline underline-offset-2 hover:opacity-80',
      })
    : null

  const stepState = (index: number): TimelineStep['state'] =>
    index < currentStepIndex
      ? 'done'
      : index === currentStepIndex
        ? 'current'
        : 'upcoming'

  const timelineSteps: TimelineStep[] = [
    {
      title: t('Packed', 'Packad'),
      date: packedDate,
      state: stepState(0),
      detail: currentStepIndex === 0 ? noteNode : null,
    },
    {
      title:
        shipment?.status === 'out_for_delivery'
          ? t('Out for delivery', 'Ute för leverans')
          : t('On the way', 'På väg'),
      date: null,
      state: stepState(1),
      detail: currentStepIndex === 1 ? noteNode : null,
    },
    {
      title: t('Delivered', 'Levererad'),
      date: etaLabel
        ? delivered
          ? etaLabel
          : t(`Est. ${etaLabel}`, `Ber. ${etaLabel}`)
        : null,
      state: stepState(2),
      detail: currentStepIndex === 2 ? noteNode : null,
    },
  ]

  const arrivalMeta = etaLabel
    ? delivered
      ? t(`Arrived ${etaLabel}`, `Anlände ${etaLabel}`)
      : t(`Arrives ${etaLabel}`, `Anländer ${etaLabel}`)
    : null
  const metaLine = [arrivalMeta, trackingNumber].filter(Boolean).join(' · ')

  const emptyDetail = t(
    'Tracking and arrival details will show here when the shipment is ready.',
    'Spårning och ankomstuppgifter visas här när leveransen är redo.'
  )

  const handleSaveTracking = async () => {
    const url = trackingLink.trim()
    const end = arrivalDate.trim() || null
    const nextStatus =
      shipment?.status || (url ? 'in_transit' : 'awaiting_shipment')

    try {
      if (shipment) {
        await updateShipment.mutateAsync({
          id: shipment.id,
          shipment_url: url || null,
          shipping_date_end: end,
          status: nextStatus,
        })
      } else {
        await createShipment.mutateAsync({
          quote_id: quoteId,
          shipment_url: url || null,
          shipping_date_end: end,
          shipping_date_start: new Date().toISOString().slice(0, 10),
          notes: customerNote.trim() || null,
          status: url ? 'in_transit' : 'awaiting_shipment',
        })
      }

      if (quotePublicId) {
        const notifiedStatus = shipment
          ? nextStatus
          : url
            ? 'in_transit'
            : 'awaiting_shipment'
        const statusOpt = SHIPMENT_STATUS_OPTIONS.find(
          (o) => o.value === notifiedStatus
        )
        const bodyLines: string[] = []
        if (statusOpt) {
          bodyLines.push(
            `${t('Delivery status', 'Leveransstatus')}: ${t(statusOpt.label)}`
          )
        }
        if (end) {
          bodyLines.push(
            `${t('Estimated arrival', 'Beräknad ankomst')}: ${end}`
          )
        }
        if (url) {
          bodyLines.push(`${t('Tracking link', 'Spårningslänk')}: ${url}`)
        }

        await orderNotifications.mutateAsync({
          kind: 'shipping_status',
          actorRole: 'admin',
          subject:
            t(
              'Delivery update for order ',
              'Leveransuppdatering för order '
            ) + `#${quotePublicId}`,
          shippingStatus: {
            quotePublicId,
            ...(bodyLines.length > 0 && { body: bodyLines.join('\n') }),
          },
        })
      }

      toast({
        title: t('Tracking updated', 'Spårning uppdaterad'),
        variant: 'success',
      })
    } catch (error) {
      console.error('Failed to save tracking:', error)
      toast({
        variant: 'destructive',
        title: t('Could not save tracking', 'Kunde inte spara spårning'),
      })
    }
  }

  const handleSaveNote = async () => {
    const notes = customerNote.trim() || null

    try {
      if (shipment) {
        await updateShipment.mutateAsync({
          id: shipment.id,
          notes,
        })
      } else {
        await createShipment.mutateAsync({
          quote_id: quoteId,
          notes,
          shipping_date_start: new Date().toISOString().slice(0, 10),
          status: 'awaiting_shipment',
        })
      }
      toast({
        title: t('Note saved', 'Meddelande sparat'),
        variant: 'success',
      })
    } catch (error) {
      console.error('Failed to save delivery note:', error)
      toast({
        variant: 'destructive',
        title: t('Could not save note', 'Kunde inte spara meddelande'),
      })
    }
  }

  if (isLoading && !demoShipment) {
    return (
      <Card
        wrapperClassName="w-full max-w-4xl !shadow-sm"
        className="gap-3 p-5 !shadow-none"
      >
        <span className="text-sm text-muted-foreground">
          {t('Loading delivery...', 'Laddar leverans...')}
        </span>
      </Card>
    )
  }

  return (
    <Card
      wrapperClassName="w-full max-w-4xl !shadow-sm"
      className="gap-5 p-6 !shadow-none"
    >
      <Row className="items-start justify-between gap-4">
        <Column className="min-w-0 gap-1">
          <Row className="items-center gap-2">
            <span
              className={cn(
                'h-2 w-2 shrink-0 rounded-full',
                STATUS_TONE[statusTone].live
              )}
            />
            <span className="text-[20px] font-semibold leading-snug tracking-[-0.02em] text-[#1A1C1E]">
              {headline}
            </span>
          </Row>
          {metaLine ? (
            <span className="text-[13px] text-[#6B7280]">{metaLine}</span>
          ) : !hasShipment ? (
            <span className="text-[13px] leading-relaxed text-[#6B7280]">
              {emptyDetail}
            </span>
          ) : null}
        </Column>

        {!isAdmin && hasTracking ? (
          <Button asChild variant="secondary" size="sm" className="shrink-0">
            <a
              href={trackingLink.trim()}
              target="_blank"
              rel="noreferrer"
            >
              {trackingCtaLabel(trackingLink.trim(), t)}
              <ArrowUpRight className="ml-1 h-3.5 w-3.5" strokeWidth={2} />
            </a>
          </Button>
        ) : null}
      </Row>

      <div className="h-px bg-border" />

      <DeliveryTimeline steps={timelineSteps} tone={statusTone} />

      {isAdmin && !demoShipment ? (
        <Column className="gap-3">
          <span className="text-xs font-medium text-muted-foreground">
            {t('Tracking link', 'Spårningslänk')}
          </span>
          <Row className="items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Link2 className="pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                value={trackingLink}
                onChange={(e) => setTrackingLink(e.target.value)}
                placeholder={t(
                  'Paste tracking URL…',
                  'Klistra in spårningslänk…'
                )}
                className="h-10 pl-9"
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="h-10 shrink-0"
              onClick={handleSaveTracking}
              disabled={isSaving}
              isLoading={isSaving}
            >
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {t('Save', 'Spara')}
            </Button>
          </Row>
          <Input
            type="date"
            label={t('Estimated arrival', 'Beräknad ankomst')}
            value={arrivalDate}
            onChange={(e) => setArrivalDate(e.target.value)}
            className="max-w-[220px]"
          />
          <Column className="gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {t('Note for customer', 'Meddelande till kund')}
            </span>
            <Textarea
              value={customerNote}
              onChange={(e) => setCustomerNote(e.target.value)}
              placeholder={t(
                'Add a message about shipping or delivery…',
                'Lägg till ett meddelande om frakt eller leverans…'
              )}
              rows={3}
              className="min-h-[84px] rounded-xl"
            />
            <Row className="justify-end">
              <Button
                variant="secondary"
                size="sm"
                className="h-10"
                onClick={handleSaveNote}
                disabled={isSaving}
                isLoading={isSaving}
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                {t('Save note', 'Spara meddelande')}
              </Button>
            </Row>
          </Column>
        </Column>
      ) : null}
    </Card>
  )
}
